import { formatLabel } from '@/utils/format'

function pickFirst(...values) {
  for (const value of values) {
    if (value == null) continue
    const text = String(value).trim()
    if (text) return text
  }
  return null
}

export function formatContextTimestamp(value) {
  if (value == null || value === '') return '—'
  const raw = String(value).trim()
  if (!raw) return '—'
  const asNum = Number(raw)
  const date =
    Number.isFinite(asNum) && /^\d+$/.test(raw)
      ? new Date(asNum)
      : new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function normalizeContextStatus(value, raw = {}) {
  const explicit = pickFirst(
    value,
    raw.status,
    raw.context_status,
    raw.contextStatus,
  )
  if (explicit) {
    const key = explicit.toUpperCase().replace(/\s+/g, '_')
    if (['DISABLED', 'INACTIVE', 'DISABLE'].includes(key)) return 'DISABLED'
    if (['ACTIVE', 'ENABLED', 'ENABLE'].includes(key)) return 'ACTIVE'
    return key
  }

  if (
    raw.is_active === false ||
    raw.isActive === false ||
    raw.is_enabled === false ||
    raw.enabled === false ||
    raw.disabled === true ||
    raw.is_disabled === true
  ) {
    return 'DISABLED'
  }

  if (
    raw.is_active === true ||
    raw.isActive === true ||
    raw.is_enabled === true ||
    raw.enabled === true
  ) {
    return 'ACTIVE'
  }

  return 'ACTIVE'
}

export function contextStatusLabel(status) {
  const key = String(status ?? '').trim().toUpperCase()
  if (!key) return '—'
  if (key === 'ACTIVE') return 'Active'
  if (key === 'DISABLED') return 'Disabled'
  return formatLabel(key)
}

export function isContextDisabled(status) {
  return String(status ?? '').trim().toUpperCase() === 'DISABLED'
}

export function normalizeContextKbFile(item, index = 0) {
  const raw = item && typeof item === 'object' ? item : {}
  const filename = pickFirst(raw.filename, raw.file_name, raw.fileName, raw.name)
  const s3Key = pickFirst(raw.s3_key, raw.s3Key, raw.key, raw.path)
  const uploadedOn = pickFirst(
    raw.uploaded_on,
    raw.uploadedOn,
    raw.uploaded_at,
    raw.uploadedAt,
  )
  const pending = uploadedOn == null || String(uploadedOn).trim() === ''

  return {
    id: s3Key || `${filename || 'file'}-${index}`,
    filename: filename || 'Untitled file',
    s3_key: s3Key,
    uploaded_on: uploadedOn,
    uploadedOnLabel: pending ? 'Pending upload' : formatContextTimestamp(uploadedOn),
    pending,
    raw,
  }
}

export function normalizeContextKbFiles(list) {
  if (!Array.isArray(list)) return []
  return list.map((item, index) => normalizeContextKbFile(item, index))
}

/**
 * Extract create/update context envelope fields used for S3 upload + record.
 * createContext always returns data with kb_files + kb_upload_metadata.
 * updateContext returns that data block only when new files were sent;
 * otherwise it is typically just { status, status_message }.
 */
export function extractContextUploadBundle(response) {
  const root = response && typeof response === 'object' ? response : {}
  const data =
    root.data && typeof root.data === 'object'
      ? root.data
      : root.items && typeof root.items === 'object' && !Array.isArray(root.items)
        ? root.items
        : root

  const nested =
    data.data && typeof data.data === 'object' ? data.data : data

  const kbFiles = normalizeContextKbFiles(
    nested.kb_files ?? nested.kbFiles ?? data.kb_files ?? data.kbFiles ?? [],
  )

  const kbUploadMetadata =
    nested.kb_upload_metadata ??
    nested.kbUploadMetadata ??
    data.kb_upload_metadata ??
    data.kbUploadMetadata ??
    {}

  const contextId = pickFirst(
    nested.context_id,
    nested.contextId,
    data.context_id,
    data.contextId,
    kbUploadMetadata.context_id,
    kbUploadMetadata.contextId,
  )

  const actionId = pickFirst(
    nested.action_id,
    nested.actionId,
    data.action_id,
    data.actionId,
  )

  const kbS3Prefix = pickFirst(
    nested.kb_s3_prefix,
    nested.kbS3Prefix,
    data.kb_s3_prefix,
    data.kbS3Prefix,
  )

  return {
    context_id: contextId,
    action_id: actionId,
    kb_s3_prefix: kbS3Prefix,
    kb_files: kbFiles,
    kb_upload_metadata:
      kbUploadMetadata && typeof kbUploadMetadata === 'object'
        ? { ...kbUploadMetadata }
        : {},
  }
}

/** @deprecated Prefer extractContextUploadBundle */
export function extractCreateContextUploadBundle(response) {
  return extractContextUploadBundle(response)
}

export const CONTEXT_KNOWN_LEAD_FIELDS = [
  'contact_name',
  'contact_email',
  'contact_number',
  'type_of_business',
  'industry',
  'message',
]

export const CONTEXT_FIELD_TYPE_OPTIONS = [
  'TEXT',
  'NUMBER',
  'DROPDOWN',
  'PHONE_NUMBER',
  'DATE',
  'TIME',
  'EMAIL',
]

/**
 * Normalize context lead_statuses from listContexts (array or AWSJSON string).
 * Values are uppercased with spaces → underscores (e.g. "In Progress" → IN_PROGRESS).
 */
export function normalizeLeadStatuses(value) {
  let source = value
  if (typeof source === 'string') {
    const trimmed = source.trim()
    if (!trimmed) return []
    try {
      source = JSON.parse(trimmed)
    } catch {
      source = trimmed.split(/[,|]/).map((part) => part.trim())
    }
  }
  if (!Array.isArray(source)) return []

  const result = []
  const seen = new Set()
  for (const entry of source) {
    const status = String(entry ?? '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_')
    if (!status || seen.has(status)) continue
    seen.add(status)
    result.push(status)
  }
  return result
}

/**
 * Normalize custom_field_definitions map:
 * { fieldName: { type, label, options?, enabled, is_system,
 *   is_unique_identifier, locked } }
 */
export function normalizeContextFieldDefinitions(value) {
  let source = value
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source)
    } catch {
      return {}
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {}

  const result = {}
  for (const [name, definition] of Object.entries(source)) {
    const fieldName = String(name ?? '').trim()
    if (!fieldName) continue
    const rawType = String(definition?.type ?? 'TEXT').trim().toUpperCase()
    const type =
      rawType === 'SELECT' || rawType === 'DROP_DOWN' || rawType === 'DROPDOWN'
        ? 'DROPDOWN'
        : rawType === 'PHONE' || rawType === 'TEL' || rawType === 'PHONE_NUMBER'
          ? 'PHONE_NUMBER'
          : rawType === 'DATETIME' || rawType === 'TIMESTAMP' || rawType === 'DATE'
            ? 'DATE'
            : rawType === 'MAIL' || rawType === 'EMAIL_ID' || rawType === 'EMAIL'
              ? 'EMAIL'
              : CONTEXT_FIELD_TYPE_OPTIONS.includes(rawType)
                ? rawType
                : 'TEXT'
    const entry = {
      type,
      label:
        String(definition?.label ?? '').trim() ||
        fieldName.replace(/_/g, ' '),
      enabled: definition?.enabled !== false,
      is_system: definition?.is_system === true,
      is_unique_identifier: definition?.is_unique_identifier === true,
      locked: definition?.locked === true,
      // System-managed value (e.g. a reminder anchor date) -- kept out of
      // Add lead / Edit lead / lead tables everywhere. Bulk-upload column
      // mapping reads custom_field_definitions directly (not through this
      // normalizer), so it stays unaffected -- the only way real data still
      // reaches the field.
      is_internal: definition?.is_internal === true,
    }
    if (entry.type === 'DROPDOWN') {
      entry.options = normalizeDropdownOptions(
        definition?.options ??
          definition?.choices ??
          definition?.values ??
          definition?.dropdown_options ??
          definition?.dropdownOptions,
      )
    }
    result[fieldName] = entry
  }
  return result
}

/** Accept array or comma-separated string option lists from the API. */
function normalizeDropdownOptions(raw) {
  if (Array.isArray(raw)) {
    return raw.map((option) => String(option ?? '').trim()).filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim()) {
    const trimmed = raw.trim()
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          return parsed.map((option) => String(option ?? '').trim()).filter(Boolean)
        }
      } catch {
        /* fall through to comma split */
      }
    }
    return trimmed
      .split(',')
      .map((option) => option.trim())
      .filter(Boolean)
  }
  return []
}

export function normalizeContextRecord(item, index = 0) {
  const raw = item && typeof item === 'object' ? item : {}
  const contextId = pickFirst(
    raw.context_id,
    raw.contextId,
    raw.id,
    raw.uuid,
  )
  const contextName = pickFirst(
    raw.context_name,
    raw.contextName,
    raw.name,
    raw.title,
  )
  const status = normalizeContextStatus(null, raw)
  const createdOn = pickFirst(
    raw.created_on,
    raw.createdOn,
    raw.created_at,
    raw.createdAt,
    raw.updated_on,
    raw.updatedOn,
  )
  const kbFiles = normalizeContextKbFiles(raw.kb_files ?? raw.kbFiles ?? [])
  const uploadedCount = kbFiles.filter((file) => !file.pending).length
  const pendingCount = kbFiles.filter((file) => file.pending).length
  const actionId = pickFirst(raw.action_id, raw.actionId)
  const actionName = pickFirst(raw.action_name, raw.actionName)
  const greeting = pickFirst(raw.greeting)
  const missionPrompt = pickFirst(
    raw.mission_prompt,
    raw.missionPrompt,
  )
  // Absent on older contexts means enabled — only an explicit false disables.
  const allowManualLeads =
    (raw.allow_manual_leads ?? raw.allowManualLeads) !== false
  const fieldDefinitions = normalizeContextFieldDefinitions(
    raw.custom_field_definitions ?? raw.customFieldDefinitions,
  )

  // The identifier lives as is_unique_identifier on a field definition.
  // No flag anywhere means "unknown / defaults to contact_number".
  const uniqueIdentifierField =
    Object.entries(fieldDefinitions).find(
      ([, definition]) => definition.is_unique_identifier,
    )?.[0] ??
    pickFirst(raw.unique_identifier_field, raw.uniqueIdentifierField) ??
    ''

  const leadStatuses = normalizeLeadStatuses(
    raw.lead_statuses ?? raw.leadStatuses,
  )

  // Shard pagination: valid getLeadShard indices are 0..current_lead_shard_index
  // inclusive. (current_lead_shard_count is server-internal — not looped on.)
  const shardIndexRaw = Number(
    raw.current_lead_shard_index ?? raw.currentLeadShardIndex,
  )
  const currentLeadShardIndex =
    Number.isFinite(shardIndexRaw) && shardIndexRaw >= 0 ? shardIndexRaw : 0

  return {
    id: contextId || `context-${index}`,
    context_id: contextId,
    context_name: contextName || 'Untitled product',
    action_id: actionId,
    action_name: actionName || '',
    greeting: greeting || '',
    mission_prompt: missionPrompt || '',
    allow_manual_leads: allowManualLeads,
    unique_identifier_field: uniqueIdentifierField || '',
    custom_field_definitions: fieldDefinitions,
    lead_statuses: leadStatuses,
    current_lead_shard_index: currentLeadShardIndex,
    status,
    statusLabel: contextStatusLabel(status),
    disabled: isContextDisabled(status),
    created_on: createdOn,
    createdOnLabel: formatContextTimestamp(createdOn),
    kb_files: kbFiles,
    fileCount: kbFiles.length,
    uploadedCount,
    pendingCount,
    filesLabel:
      kbFiles.length === 0
        ? 'No files'
        : pendingCount > 0
          ? `${uploadedCount}/${kbFiles.length} uploaded`
          : `${kbFiles.length} file${kbFiles.length === 1 ? '' : 's'}`,
    // Dashboard Digest -- promoted to top-level (not just buried in `raw`
    // below) so ContextFormDialog.vue's own populate-from-existing-context
    // reads (props.context.dashboard_digest_*) actually see saved values
    // instead of always looking freshly-empty after every list reload, the
    // same bug this normalizer's org-level counterpart (mapOrganizationFields
    // in utils/session.js) had until just now.
    send_dashboard_digest_by_email: raw.send_dashboard_digest_by_email === true,
    dashboard_digest_email_addresses: Array.isArray(raw.dashboard_digest_email_addresses)
      ? raw.dashboard_digest_email_addresses
      : [],
    send_dashboard_digest_by_whatsapp: raw.send_dashboard_digest_by_whatsapp === true,
    dashboard_digest_whatsapp_numbers: Array.isArray(raw.dashboard_digest_whatsapp_numbers)
      ? raw.dashboard_digest_whatsapp_numbers
      : [],
    dashboard_digest_cc_agents: raw.dashboard_digest_cc_agents === true,
    dashboard_digest_times: Array.isArray(raw.dashboard_digest_times) ? raw.dashboard_digest_times : [],
    // Independent weekly/monthly cadences alongside the daily times above.
    dashboard_digest_weekly_enabled: raw.dashboard_digest_weekly_enabled === true,
    dashboard_digest_weekly_day: raw.dashboard_digest_weekly_day || '',
    dashboard_digest_weekly_time: raw.dashboard_digest_weekly_time || '',
    dashboard_digest_monthly_enabled: raw.dashboard_digest_monthly_enabled === true,
    dashboard_digest_monthly_day: raw.dashboard_digest_monthly_day ?? null,
    dashboard_digest_monthly_time: raw.dashboard_digest_monthly_time || '',
    // Per-report-type saved column config ({ callLog: [...], ... }) -- same
    // "promote to top-level, don't leave it buried in raw" fix
    // dashboard_digest_* needed above, for the exact same reason: without
    // this the new Report columns tab would always see "nothing saved"
    // regardless of what's actually stored.
    report_column_config: (raw.report_column_config && typeof raw.report_column_config === 'object')
      ? raw.report_column_config
      : {},
    raw,
  }
}

export function normalizeContextsList(rawList) {
  if (!Array.isArray(rawList)) return []
  return rawList.map((item, index) => normalizeContextRecord(item, index))
}
